import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError as observableThrowError, of } from 'rxjs';
import { catchError, map, publishReplay, refCount } from 'rxjs/operators';
import { ISportTO } from 'src/app/models/sportHierarchy/transportObjects/sportTO.interface';
import { BaseService } from './base.service';
import { ISportWithSelectionsTO } from 'src/app/models/sportHierarchy/transportObjects/sportWithSelectionsTO.interface';
import { ISpecialOfferButtonTO } from 'src/app/models/sportHierarchy/transportObjects/specialOfferButtonTO.interface';
import { EventVM } from 'src/app/models/event/event';
import { LoaderService } from '../loader.service';
import { UserActivityService } from '../userActivity.service';
import { NotificationService } from '../notification.service';
import { IOddSheets } from 'src/app/models/oddSheets/oddSheets.interface';
import { IOddSheetsTO } from 'src/app/models/oddSheets/transportObjects/oddSheetsTO.interface';
import { EventMappingType } from 'src/app/models/enums';
import { ISportForLiveTreeTO } from 'src/app/models/sportHierarchy/transportObjects/sportForLiveTreeTO.interface';
import { StorageService } from '../util/storage.service';
import { DeployParams } from 'src/app/deployParams';
import { environment } from 'src/environments/environment';

@Injectable()
export class SportsProviderService extends BaseService {
  constructor(
    private http: HttpClient,
    protected loaderService: LoaderService,
    protected userActivityService: UserActivityService,
    protected notificationService: NotificationService,
    protected storage: StorageService) {
    super(loaderService, userActivityService, notificationService, storage);
  }

  public getSports(dateFrom: Date, dateTo: Date, isLive: boolean = false, eventMappingTypes: EventMappingType[] = null): Observable<ISportTO[]> {
    let params = new HttpParams();
    
    if (!eventMappingTypes || eventMappingTypes.length == 0) {
      eventMappingTypes = [
        EventMappingType.StandardEvent,
        EventMappingType.EventOfDay,
        EventMappingType.SuperBetEvent,
        EventMappingType.Live,
        EventMappingType.Special]
    }
    if (eventMappingTypes) {
      for (const type of eventMappingTypes) {
        params = params.append('eventMappingTypes', type.toString());
      }
    }

    const url: string = this.offerController + 'tree/null/true/true/true/'
      // later remove replace
      + dateFrom.toISOString().replace('Z', '') + '/'
      + dateTo.toISOString().replace('Z', '') + '/' + isLive;
    return this.http.get<ISportTO[]>(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as ISportTO[]),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  public getLiveSports(sportId: number, includeEvents: boolean = true, includeBets: boolean = true, onlySelectedBetTypes: boolean = false): Observable<ISportForLiveTreeTO[]> {
    const params = new HttpParams();
    const url: string = this.offerController + 'livetree/5/' + sportId + '/' + includeEvents.toString() + '/' + includeBets.toString() + '/' + onlySelectedBetTypes.toString();
    return this.http.get<ISportForLiveTreeTO[]>(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as ISportForLiveTreeTO[]),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  getSpecialOfferButtons(dateFrom: Date, dateTo: Date): Observable<ISpecialOfferButtonTO[]> {
    const params = new HttpParams()
      .set('dateFrom', dateFrom.toISOString().replace('Z', ''))
      .set('dateTo', dateTo.toISOString().replace('Z', ''));
    const url: string = this.offerController + 'SpecialOfferButtons';
    return this.http.get<ISpecialOfferButtonTO[]>(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as ISpecialOfferButtonTO[]),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  getSportForLiveEvent(event: EventVM): Observable<ISportForLiveTreeTO> {
    const params = new HttpParams();
    const url: string = this.offerController + 'GetSportWithSingleCompetition/' + event.sportId + '/' + event.regionId + '/' + event.competitionId + '/5';
    return this.http.get<ISportForLiveTreeTO>(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as ISportForLiveTreeTO),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  getMostPopularSports(numberOfSports: number, isLive: boolean, eventMappingTypes: EventMappingType[] = null): Observable<ISportTO[]> {
    let params = new HttpParams();
    if (!eventMappingTypes || eventMappingTypes.length == 0) {
      eventMappingTypes = [
        EventMappingType.StandardEvent,
        EventMappingType.EventOfDay,
        EventMappingType.SuperBetEvent,
        EventMappingType.Live,
        EventMappingType.Special]
    }
    if (eventMappingTypes) {
      for (const type of eventMappingTypes) {
        params = params.append('eventMappingTypes', type.toString());
      }
    }
    const url: string = this.offerController + 'mostPopularSports/' + numberOfSports.toString() + '/' + isLive.toString();
    return this.http.get<ISportTO[]>(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as ISportTO[]),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  getSportsStartingSoon(numberOfSports: number, isLive: boolean, nextHoursTo: number, eventMappingTypes: EventMappingType[] = null): Observable<ISportTO[]> {
    const dateTo = new Date();
    dateTo.setHours(dateTo.getHours() + nextHoursTo);
    let params = new HttpParams();
    if (!eventMappingTypes || eventMappingTypes.length == 0) {
      eventMappingTypes = [
        EventMappingType.StandardEvent,
        EventMappingType.EventOfDay,
        EventMappingType.SuperBetEvent,
        EventMappingType.Live,
        EventMappingType.Special]
    }
    if (eventMappingTypes) {
      for (const type of eventMappingTypes) {
        params = params.append('eventMappingTypes', type.toString());
      }
    }
    const url: string = this.offerController + 'sportsWithEventsStartingSoon/' + numberOfSports.toString() + '/' + isLive.toString() + '/' + dateTo.toISOString().replace('Z', '');
    return this.http.get<ISportTO[]>(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as ISportTO[]),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  getOddsheets(): Observable<IOddSheetsTO> {
    const params = new HttpParams();
    const url: string = environment.baseURL + 'OddsSheet';
    return this.http.get<IOddSheetsTO>(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as IOddSheetsTO),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        return of(null);
      }),
    );
  }

  getSportTreeForResults(dateFrom: Date, dateTo: Date): Observable<ISportTO[]> {
    const url: string = this.offerController + 'SportTreeWithEventResults/' + dateFrom.toISOString().replace('Z', '') + '/' + dateTo.toISOString().replace('Z', '');
    const params = new HttpParams();
    return this.http.get<ISportTO[]>(url, { headers: this.httpHeaders, params }).pipe(
      map((response) => response as ISportTO[]),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        observableThrowError(this.handleError(err));
        return [];
      }),
    );
  }

  downloadListPdf(fileName: string, isMainList: boolean): Observable<Blob> {
    const params = new HttpParams();
    const options = { headers: this.httpHeaders, params };
    const url: string = this.baseUrlWebClientController + 'downloadList/' + fileName + '/' + isMainList;
    return this.http.get(url, { ...options, responseType: 'blob' });
  }
}
